/**
 * Reading a link somebody pasted in, which is the inverse of `organiserUrl` in
 * `components/ShareButton.ts` — change one and check the other.
 *
 * This exists because a link is the *only* way back into a game you organise, and a
 * link is exactly what can't cross into an app on a phone's home screen: an installed
 * PWA gets its own storage, and a tapped link opens the browser instead. So the
 * organiser has to be able to put the link in by hand, and by hand means it arrives
 * mangled — wrapped by a mail client, missing its scheme, carrying the full stop that
 * ended the sentence it was in.
 *
 * Pure, and deliberately free of imports: the tests all run in the Workers pool with no
 * DOM, so anything worth pinning has to live somewhere a test can reach.
 */

/**
 * The shape a token has to have before it is worth sending anywhere.
 *
 * Not a security check — it's a truncation check. The tokens are `crypto.randomUUID()`,
 * so anything short or containing punctuation was cut short in transit rather than
 * guessed. Catching that here is what lets the screen say "this looks cut short"
 * instead of accepting it: a well-formed-but-wrong token fails at the WebSocket
 * handshake, and a browser never shows script the 401 behind a failed upgrade — it
 * arrives as an ordinary close, so the app would sit there reconnecting, "offline",
 * forever.
 */
export const VALID_TOKEN = /^[A-Za-z0-9-]{8,100}$/;

/** Join codes are four unambiguous characters (`worker/lib/ids.ts`). */
const JOIN_CODE = /^[A-Za-z0-9]{4}$/;

/**
 * The link itself, wherever it sits in whatever was pasted around it.
 *
 * Sharing an organiser link sends a paragraph along with it — the warning about the
 * link never expiring — and the share sheet hands the whole lot over as one lump. So
 * the link has to be *found* rather than assumed to be the entire paste.
 */
const ORGANISER_LINK = /t\/([A-Za-z0-9]{4})\/organi[sz]er(?:\/([A-Za-z0-9-]*))?/i;

/** Enough of the word to know a link was meant, when the rest is too mangled to read. */
const ORGANISER_WORD = /\/organi[sz]er/i;

/**
 * A token in the shape `crypto.randomUUID()` makes them.
 *
 * Used for exactly one thing: knowing where a token *stops* when prose runs straight
 * into it. Whitespace has to come out — a mail client folding a line mid-token is the
 * commonest damage there is — but taking it out also welds any following sentence onto
 * the end, and the two are indistinguishable by then. Never used to decide whether a
 * token is valid, so a token that stops being a UUID one day still works; it would only
 * lose the ability to have prose after it on the same paste.
 */
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export type PastedLink =
  | { kind: "organiser"; code: string; token: string }
  /** A bare token with no game code around it — ask for the code separately. */
  | { kind: "tokenOnly"; token: string }
  /** The invite link, not the organiser one. A common and very understandable mix-up. */
  | { kind: "join"; code: string }
  /** Recognisably an organiser link, with the token missing or truncated. */
  | { kind: "incomplete"; code: string | null }
  | null;

/**
 * Whether a string on its own — no link around it — is someone offering a token.
 *
 * Stricter than `VALID_TOKEN`, and only for *classifying* a paste: with no URL to go
 * on, "hello there" is a run of letters long enough to pass the shape check, and
 * answering that with "now enter your game code" sends someone one step further from
 * the thing that would actually help them. Requiring the hyphens and the length of a
 * `crypto.randomUUID()` tells prose apart from a token without the rest of this module
 * caring what tokens look like — a link that carries its own code is never asked.
 */
function looksLikeBareToken(s: string): boolean {
  return VALID_TOKEN.test(s) && s.includes("-") && s.length >= 16;
}

/** Drops a leading `//host` or `host`, so what's left starts at the path or the hash. */
function stripHost(input: string): string {
  const withScheme = /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:)?\/\/[^/#?]+/.exec(input);
  if (withScheme) return input.slice(withScheme[0].length);

  // `divotgolf.club/#/t/…` — what you get from a phone that hid the scheme when the
  // link was copied out of the address bar.
  const bare = /^[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+(?::\d+)?(?=[/#])/.exec(input);
  if (bare) return input.slice(bare[0].length);

  return input;
}

export function parsePastedLink(raw: string): PastedLink {
  // Every character of whitespace is removed rather than trimmed, because the usual
  // damage isn't padding — it's a hard wrap inserted into the middle of the token by
  // whatever app the link travelled through. A real link has no whitespace in it, so
  // there is nothing to lose by closing every gap.
  let s = raw.replace(/\s+/g, "");
  if (!s) return null;
  // Some share sheets hand back the `#` percent-encoded.
  s = s.replace(/%23/gi, "#");

  // The organiser link is looked for anywhere in the paste, not assumed to be all of
  // it — matched on `t/<code>/organizer/` rather than on where the `#` falls, so a
  // hashtag in the surrounding message can't be mistaken for the start of the route.
  const found = ORGANISER_LINK.exec(s);
  if (found) {
    const code = found[1]!.toUpperCase();
    let token = found[2] ?? "";
    const uuid = UUID_PREFIX.exec(token);
    // A UUID knows its own length, so anything welded on behind it comes off. Failing
    // that, trim the punctuation the link picked up from the sentence it was in — by
    // character class rather than a list of suspects, the token's alphabet being known.
    token = uuid ? uuid[0] : token.replace(/[^A-Za-z0-9-]+$/, "");
    if (VALID_TOKEN.test(token)) return { kind: "organiser", code, token };
    return { kind: "incomplete", code };
  }
  // Recognisably meant to be one, too damaged to read a code out of.
  if (ORGANISER_WORD.test(s)) return { kind: "incomplete", code: null };

  const rest = stripHost(s);
  // For everything else, what this app routes on lives after the `#`. Taking the part
  // after the first one also drops any `?utm=…` a tracker bolted on ahead of it.
  const hashAt = rest.indexOf("#");
  const path = hashAt >= 0 ? rest.slice(hashAt + 1) : rest.split("?")[0]!;
  const segs = path.split("/").filter(Boolean);
  if (segs.length === 0) return null;

  // Tested before the token check, and it can't collide with it: a join code is four
  // characters and a token is at least eight.
  if (segs[0] === "join" && segs[1] && JOIN_CODE.test(segs[1])) {
    return { kind: "join", code: segs[1].toUpperCase() };
  }
  if (segs.length === 1 && JOIN_CODE.test(segs[0]!)) {
    return { kind: "join", code: segs[0]!.toUpperCase() };
  }

  if (segs.length === 1 && looksLikeBareToken(segs[0]!)) {
    return { kind: "tokenOnly", token: segs[0]! };
  }

  return null;
}
